import AccountList from '@client/models/store/accountList';
import {CUSTODIANS} from '@client/util/constants/custodian';
import {Logger, Category} from '@simpleinvest/front-end-logger';
import MicroDepositStatus from '@client/enums/micro-deposit-status-type';
import identityAccountActionType from '@client/enums/identityAccountActionType';
import AccountActionArrayKeys from '@client/enums/accountActionArrayKeys';
import {AccountRequestStatuses} from '@client/enums/accountRequestStatuses';
import AccountRequestInitiatedFrom from '@client/enums/accountRequestInitiatedFrom';
import {displayErrorBanner} from '@client/events/notification-bus';

// eslint-disable-next-line import/no-cycle
import accountRequestService from '@client/services/accountRequest';
// eslint-disable-next-line import/no-cycle
import goalService from '@client/services/goal';

const logger = new Logger({
  category: Category.Accounts,
  fileName: 'accountList.js',
});

const stateObj = () => new AccountList();

export const transformAccount = (item) => ({
  accountId: item.accountId,
  name: item.title,
  accountNumber: item.accountNumber,
  bankAccountId: item.bankAccountId,
  accountType: item.type,
  balances: {current: Number(item.aum)},
  custodianCode: CUSTODIANS.APEX.code,
  isAccountInLiquidationProcess: item.isAccountInLiquidationProcess,
  actions: item.actions,
  accountRequestId: item.requestId,
  goalIds: item.goalIds,
});

const gettersObj = {
  accountList: (state) => state.accountList,
  accountIds: (state) => state.accountList.map((account) => account.accountId).filter((a) => a !== null),
  isAccountListLoaded: (state) => state.isAccountListLoaded,
  accountCustodians: (state) => {
    if (!state.accountList) {
      return [];
    }

    const liveAccounts = state.accountList.map((account) => account.custodianName);
    const custodians = new Set(liveAccounts);

    return [...custodians];
  },
  hasPlatformAccounts: (state) =>
    // eslint-disable-next-line no-undefined
    state.accountList !== undefined && state.accountList !== null && state.accountList.length > 0,
  platformAccountsBalanceSum: (state, getters) => {
    let sum = 0;

    if (getters.hasPlatformAccounts) {
      getters.accountList.forEach((account) => {
        if (account.isLive) {
          sum += account.aum;
        }
      });
    }

    return sum;
  },
  platformAccountsBalanceSumNoLiquidation: (state, getters) => {
    let sum = 0;

    if (getters.hasPlatformAccounts) {
      getters.accountList
        .filter((item) => item.isLive)
        .filter((item) => !item.isAccountInLiquidationProcess)
        .forEach((account) => {
          sum += account.aum;
        });
    }

    return sum;
  },

  apexLiveAccounts: (state) => {
    const allAccounts = state.accountList || [];

    return (
      allAccounts
        .filter((item) => item.isLive)
        .filter((item) => item.custodianId === CUSTODIANS.APEX.id)
        // eslint-disable-next-line unicorn/no-fn-reference-in-iterator
        .map((account) => transformAccount(account))
    );
  },
  accountsForGoalId: (state) => (goalId) => {
    const allAccounts = state.accountList || [];
    const goalIdStr = goalId.toString();

    return allAccounts.filter((item) => item.goalIds?.includes(goalIdStr));
  },
  findAccountByAccountId(state, getters) {
    return (accountId) => getters.accountList.find((item) => Number(item.accountId) === Number(accountId));
  },
  findAccountByRequestId(state, getters) {
    return (requestId) => getters.accountList.find((item) => Number(item.requestId) === Number(requestId));
  },
  doesAccountHaveLiquidationInProcess(state, getters) {
    return (accountId) => {
      const matchingAccount = getters.findAccountByAccountId(accountId);

      return matchingAccount.isAccountInLiquidationProcess;
    };
  },
  doesAccountHaveToVerifyBankLink(state, getters) {
    return (accountId) => {
      const matchingAccount = getters.findAccountByAccountId(accountId);

      return matchingAccount.verifyBank;
    };
  },

  isAccountMicrodepositPendingManualVerification(state, getters) {
    return (accountId) => {
      const {publicToken} = getters.findAccountByAccountId(accountId);

      return publicToken?.verification_status === MicroDepositStatus.PendingManual;
    };
  },
  getIdentityPendingAlertByAccountId(state, getters) {
    return (accountId) => {
      const identityAlerts = new Set([
        identityAccountActionType.verifyDocumentation,
        identityAccountActionType.documentationRequired,
        identityAccountActionType.identityPending,
      ]);

      const identityActions = new Set([identityAccountActionType.documentationRequired]);

      const account = getters.findAccountByAccountId(accountId);
      let identityPendingAlerts = null;

      if (account?.actions?.length > 0) {
        identityPendingAlerts = account.actions.find((action) => identityAlerts.has(action)) || null;
      }

      return {
        alert: identityPendingAlerts,
        isAction: identityActions.has(identityPendingAlerts) || false,
      };
    };
  },
  getKycAlertByRequestId(state, getters) {
    return (requestId) => {
      const kycAlerts = new Set([AccountActionArrayKeys.KycPendingApproval, AccountActionArrayKeys.KycVerifyIdentity]);

      const account = getters.findAccountByRequestId(requestId);
      let kycAlert = null;

      if (account?.actions?.length > 0) {
        kycAlert = account.actions.find((action) => kycAlerts.has(action)) || null;
      }

      const kycActions = new Set([AccountActionArrayKeys.KycVerifyIdentity]);

      return {
        alert: kycAlert,
        isAction: kycActions.has(kycAlert) || false,
      };
    };
  },
  getAccountOpeningAlert() {
    return (account) => {
      const alerts = new Set([
        AccountRequestStatuses.FinishOwnerInformation,
        AccountRequestStatuses.FinishBeneficiaries,
        AccountRequestStatuses.FinishAdditionalInformation,
        AccountRequestStatuses.FinishReview,
        AccountRequestStatuses.PendingVerification,
      ]);

      const matchingAlert = alerts.has(account.accountRequestStatus) || false;
      let platformRequestInitiatedFrom = null;

      if (matchingAlert) {
        platformRequestInitiatedFrom =
          account.requestInitiatedFrom === AccountRequestInitiatedFrom.Advisor
            ? AccountRequestInitiatedFrom.Advisor
            : AccountRequestInitiatedFrom.Client;
      }

      return {
        foundMatchingAlert: matchingAlert,
        platformRequestInitiatedFrom,
      };
    };
  },
  isAccountInErrorState() {
    return (account) => {
      const isAccountInError = account.accountRequestStatus === AccountRequestStatuses.Error;

      return isAccountInError;
    };
  },
  hasAccountActions(state, getters, rootState, rootGetters) {
    return getters.accountList.some((account) => {
      const {alert: kycAlert, isAction: isKycAction} = getters.getKycAlertByRequestId(account.requestId);

      const {alert: identityAlert, isAction: isIdentityAction} = getters.getIdentityPendingAlertByAccountId(
        account.accountId
      );
      const {foundMatchingAlert: foundAccountOpeningAlert} = getters.getAccountOpeningAlert(account);

      return (
        !!rootGetters['transfers/findRequestedTransferByAccountId'](account.accountId) ||
        !!rootGetters['transfers/findRequestedBankLinkByAccountId'](account.accountId) ||
        (kycAlert !== null && isKycAction) ||
        foundAccountOpeningAlert ||
        getters.isAccountMicrodepositPendingManualVerification(account.accountId) ||
        (identityAlert !== null && isIdentityAction) ||
        getters.isAccountInErrorState(account)
      );
    });
  },
};

const actions = {
  async loadAccountList(context) {
    context.commit('setIsAccountListLoaded', false);
    const userId = context.rootGetters['cognitoUser/userId'];

    // This method will call the overview load method to call the bff
    await context.dispatch('overview/getOverviewData', {userId}, {root: true});
  },
  async getAccountsUnAttachedwithGoal(context, goalId) {
    const userId = context.rootGetters['cognitoUser/userId'];
    const apiCall = await goalService.getAccountsUnAttachedwithGoal(userId, goalId);

    if (apiCall && apiCall.status === 'success') {
      return apiCall.result;
    }

    return null;
  },
  async deleteAccountByReqId(context, reqId) {
    try {
      await accountRequestService.deleteAccountRequest(reqId);
      await context.dispatch('loadAccountList');
    } catch (error) {
      const tags = [
        {
          key: 'requestId',
          value: reqId,
        },
      ];

      logger.addBreadcrumb({
        callingFuncName: 'deleteAccountByReqId',
        message: `deleteAccountByReqId error caught`,
      });

      logger.logException({
        error,
        tags,
      });

      displayErrorBanner('Something went wrong canceling the account opening request. Please try again later.');
    }
  },
  setAccountListDirty({commit}) {
    commit('setIsAccountListLoaded', false);
  },

  setAccountListClean({commit}) {
    commit('setIsAccountListLoaded', true);
  },
};

const mutations = {
  saveAccountList(state, accountInfo) {
    state.accountList = accountInfo.accounts;
    state.isAccountListLoaded = true;
  },

  setIsAccountListLoaded(state, isAccountListLoaded) {
    state.isAccountListLoaded = isAccountListLoaded;
  },
};

export default {
  namespaced: true,
  state: stateObj,
  getters: gettersObj,
  actions,
  mutations,
};
